import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import localforage from 'localforage';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import Canvas from '../layouts/dahboard_canvas';
import DashboardFooter from '../layouts/dashboard_footer';
import EHRNav from '../layouts/components/EHRNav';
import { useBaseUrl } from '../../utils/utils';
import { usePatient } from '../../contexts/PatientContext';

type PatientSummary = {
    id?: number | string;
    patient_id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    gender?: string;
    date_of_birth?: string;
    photo?: string;
};

type SelectOption = {
    value: string;
    label: string;
    description?: string;
};

type RecordTypeId =
    | 'outpatient_consultation'
    | 'history_and_physical'
    | 'progress_note'
    | 'emergency_note'
    | 'procedure_note'
    | 'discharge_summary';

type TypeSpecificField = {
    key: string;
    label: string;
    placeholder: string;
    rows?: number;
    required?: boolean;
};

type RecordTypeDefinition = {
    id: RecordTypeId;
    label: string;
    shortLabel: string;
    summary: string;
    documentationSummary: string;
    titleSuggestion: string;
    icon: string;
    color: string;
    fields: TypeSpecificField[];
};

type CreateMode = 'idle' | 'new' | 'existing';

type PatientForm = {
    first_name: string;
    middle_name: string;
    last_name: string;
    other_name: string;
    email: string;
    date_of_birth: string;
    gender: string;
};

type RecordForm = {
    admNo: string;
    title: string;
    recordType: RecordTypeId | '';
    recordStatus: string;
    privacyLevel: string;
    isConfidential: boolean;
    requiresFollowUp: boolean;
    emergencyContactName: string;
    emergencyContactPhone: string;
    emergencyContactRelationship: string;
    insuranceInformation: string;
    chiefComplaint: string;
    historyPresentIllness: string;
    hpiOnset: string;
    hpiDuration: string;
    hpiLocation: string;
    hpiCharacteristics: string;
    hpiAggravatingFactors: string;
    hpiAlleviatingFactors: string;
    hpiRelatedSymptoms: string;
    hpiTreatmentsTried: string;
    medicalHistory: string;
    allergies: string;
    currentMedications: string;
    previousSurgeries: string;
    previousHospitalizations: string;
    pastInjuries: string;
    familyHistory: string;
    socialHistory: string;
    occupation: string;
    smokingStatus: string;
    alcoholUse: string;
    drugUse: string;
    exerciseHabits: string;
    diet: string;
    livingSituation: string;
    reviewOfSystems: string;
    physical: string;
    generalAppearance: string;
    assessment: string;
    treatmentPlan: string;
    patientEducation: string;
    followUpInstructions: string;
    diagnosisCode: string;
    diagnosisDescription: string;
    providerNotes: string;
    providerSignature: boolean;
    advanceDirectives: string;
    immunizationHistory: string;
    growthCharts: string;
    mentalHealthAssessment: string;
    consentForms: string;
    temp: string;
    heart: string;
    bp: string;
    bg: string;
    height: string;
    weight: string;
    resp: string;
    oxygen: string;
    vitalNotes: string;
    painScore: string;
    labTest: string;
    labResult: string;
    labRef: string;
    typeSpecificData: Record<string, string>;
};

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';
const BLUE = '#2563eb';
const BLUE_SOFT = 'rgba(37, 99, 235, 0.08)';
const SLATE = '#475569';
const SLATE_SOFT = 'rgba(71, 85, 105, 0.10)';

const cardStyle: React.CSSProperties = {
    border: '1px solid rgba(15, 23, 42, 0.06)',
    borderRadius: 18,
    background: '#fff',
    boxShadow: '0 14px 40px rgba(15, 23, 42, 0.06)',
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.35px',
    color: 'var(--uzima-shell-text-muted)',
    marginBottom: 6,
};

const emptyPatientForm = (): PatientForm => ({
    first_name: '',
    middle_name: '',
    last_name: '',
    other_name: '',
    email: '',
    date_of_birth: '',
    gender: '',
});

const emptyRecordForm = (): RecordForm => ({
    admNo: '',
    title: '',
    recordType: '',
    recordStatus: 'draft',
    privacyLevel: 'standard',
    isConfidential: false,
    requiresFollowUp: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    insuranceInformation: '',
    chiefComplaint: '',
    historyPresentIllness: '',
    hpiOnset: '',
    hpiDuration: '',
    hpiLocation: '',
    hpiCharacteristics: '',
    hpiAggravatingFactors: '',
    hpiAlleviatingFactors: '',
    hpiRelatedSymptoms: '',
    hpiTreatmentsTried: '',
    medicalHistory: '',
    allergies: '',
    currentMedications: '',
    previousSurgeries: '',
    previousHospitalizations: '',
    pastInjuries: '',
    familyHistory: '',
    socialHistory: '',
    occupation: '',
    smokingStatus: '',
    alcoholUse: '',
    drugUse: '',
    exerciseHabits: '',
    diet: '',
    livingSituation: '',
    reviewOfSystems: '',
    physical: '',
    generalAppearance: '',
    assessment: '',
    treatmentPlan: '',
    patientEducation: '',
    followUpInstructions: '',
    diagnosisCode: '',
    diagnosisDescription: '',
    providerNotes: '',
    providerSignature: false,
    advanceDirectives: '',
    immunizationHistory: '',
    growthCharts: '',
    mentalHealthAssessment: '',
    consentForms: '',
    temp: '',
    heart: '',
    bp: '',
    bg: '',
    height: '',
    weight: '',
    resp: '',
    oxygen: '',
    vitalNotes: '',
    painScore: '',
    labTest: '',
    labResult: '',
    labRef: '',
    typeSpecificData: {},
});

const RECORD_TYPE_DEFINITIONS: RecordTypeDefinition[] = [
    {
        id: 'outpatient_consultation',
        label: 'Outpatient Consultation Note',
        shortLabel: 'Consultation Note',
        summary: 'For new ambulatory visits, specialty reviews, or a standard doctor consultation encounter.',
        documentationSummary: 'Capture the consultation question, symptom course, prior workup, and care coordination decisions.',
        titleSuggestion: 'Outpatient Consultation Note',
        icon: 'bx bx-clinic',
        color: ACCENT,
        fields: [
            { key: 'consult_reason', label: 'Consultation Question', placeholder: 'What specific issue or referral question is being addressed?', rows: 3, required: true },
            { key: 'symptom_course', label: 'Symptom Timeline and Pattern', placeholder: 'Document chronology, severity, triggers, and progression.', rows: 4, required: true },
            { key: 'prior_workup', label: 'Prior Evaluation or Treatment', placeholder: 'Prior tests, previous clinicians, medications, or interventions.' , rows: 3 },
            { key: 'differential_focus', label: 'Differential Considerations', placeholder: 'Key possibilities considered and why they matter.', rows: 3 },
            { key: 'coordination_plan', label: 'Referral / Coordination Plan', placeholder: 'Referrals, handoffs, shared care, or external coordination details.', rows: 3 },
            { key: 'patient_goal', label: 'Patient Goal or Priority', placeholder: 'What outcome matters most to the patient?', rows: 2 },
        ],
    },
    {
        id: 'history_and_physical',
        label: 'History and Physical (H&P)',
        shortLabel: 'H&P',
        summary: 'For initial assessment, admission-style documentation, or comprehensive baseline evaluation.',
        documentationSummary: 'Capture history source, reliability, baseline function, risk profile, and a complete initial assessment picture.',
        titleSuggestion: 'History and Physical Note',
        icon: 'bx bx-body',
        color: BLUE,
        fields: [
            { key: 'admission_reason', label: 'Admission / Assessment Reason', placeholder: 'Why is the patient being assessed or admitted?', rows: 3, required: true },
            { key: 'history_source', label: 'Source of History', placeholder: 'Patient, caregiver, records, EMS, referral note, etc.', required: true },
            { key: 'history_reliability', label: 'History Reliability', placeholder: 'Is the history complete, limited, or uncertain? Why?' },
            { key: 'baseline_function', label: 'Baseline Functional Status', placeholder: 'Baseline mobility, cognition, ADLs, and performance before this illness.', rows: 3 },
            { key: 'risk_profile', label: 'Risk Factors and Safety Concerns', placeholder: 'Falls, frailty, substance use, safeguarding, or social risk details.', rows: 3 },
            { key: 'immunization_review', label: 'Immunization / Preventive Review', placeholder: 'Relevant immunization or preventive screening status.', rows: 2 },
        ],
    },
    {
        id: 'progress_note',
        label: 'Progress Note',
        shortLabel: 'Progress Note',
        summary: 'For daily reviews, follow-up visits, monitoring response to therapy, or interval reassessments.',
        documentationSummary: 'Capture interval changes, treatment response, medication adjustments, barriers, and next milestones.',
        titleSuggestion: 'Clinical Progress Note',
        icon: 'bx bx-line-chart',
        color: '#7c3aed',
        fields: [
            { key: 'interval_events', label: 'Interval Events', placeholder: 'What changed since the previous review?', rows: 3, required: true },
            { key: 'treatment_response', label: 'Response to Treatment', placeholder: 'Clinical improvement, deterioration, or no change.', rows: 3, required: true },
            { key: 'medication_updates', label: 'Medication Changes', placeholder: 'New meds, stopped meds, dose changes, adherence concerns.', rows: 3 },
            { key: 'pending_results', label: 'Pending Results / Actions', placeholder: 'Outstanding labs, consults, imaging, or tasks.', rows: 3 },
            { key: 'care_barriers', label: 'Barriers to Recovery or Care', placeholder: 'Financial, social, transport, adherence, or cognitive barriers.', rows: 3 },
            { key: 'next_milestone', label: 'Next Review Goal', placeholder: 'What should be achieved before the next review?', rows: 2 },
        ],
    },
    {
        id: 'emergency_note',
        label: 'Emergency Department Note',
        shortLabel: 'Emergency Note',
        summary: 'For acute and urgent presentations, triage documentation, emergency stabilization, and ED disposition.',
        documentationSummary: 'Capture triage acuity, arrival context, emergency interventions, reassessments, and disposition decisions.',
        titleSuggestion: 'Emergency Department Note',
        icon: 'bx bx-ambulance',
        color: '#dc2626',
        fields: [
            { key: 'triage_acuity', label: 'Triage Acuity', placeholder: 'Triage level, acuity, or immediate risk classification.', required: true },
            { key: 'arrival_mode', label: 'Arrival Mode', placeholder: 'Walk-in, ambulance, referral, police, caregiver, etc.', required: true },
            { key: 'context_of_event', label: 'Mechanism / Context of Event', placeholder: 'Trauma mechanism, exposure, collapse context, onset setting.', rows: 3 },
            { key: 'emergency_interventions', label: 'Emergency Interventions', placeholder: 'Resuscitation, stabilization, medications, procedures, monitoring.', rows: 4, required: true },
            { key: 'reassessment', label: 'Reassessment Findings', placeholder: 'Patient response after initial emergency management.', rows: 3 },
            { key: 'disposition', label: 'Disposition Decision', placeholder: 'Admit, observe, discharge, transfer, or refer, with rationale.', rows: 3, required: true },
        ],
    },
    {
        id: 'procedure_note',
        label: 'Procedure Note',
        shortLabel: 'Procedure Note',
        summary: 'For bedside procedures, interventions, wound care, aspirations, insertions, or minor theatre documentation.',
        documentationSummary: 'Capture indication, consent, technique, findings, complications, and immediate post-procedure plan.',
        titleSuggestion: 'Procedure Note',
        icon: 'bx bx-plus-medical',
        color: '#ea580c',
        fields: [
            { key: 'procedure_name', label: 'Procedure Name', placeholder: 'Enter the exact procedure performed.', required: true },
            { key: 'procedure_indication', label: 'Indication for Procedure', placeholder: 'Why was the procedure needed?', rows: 3, required: true },
            { key: 'consent_confirmation', label: 'Consent Confirmation', placeholder: 'Document consent, witness, and communication details.', rows: 2, required: true },
            { key: 'anesthesia', label: 'Anesthesia / Analgesia', placeholder: 'Local anesthesia, sedation, pain control, or none used.', rows: 2 },
            { key: 'procedure_findings', label: 'Procedure Findings', placeholder: 'What was found or achieved during the procedure?', rows: 3, required: true },
            { key: 'complications', label: 'Complications', placeholder: 'Any adverse events, bleeding, pain, failed attempts, or none.', rows: 2 },
            { key: 'post_procedure_plan', label: 'Post-Procedure Instructions', placeholder: 'Observation, wound care, medication, review timing, escalation signs.', rows: 3, required: true },
        ],
    },
    {
        id: 'discharge_summary',
        label: 'Discharge Summary',
        shortLabel: 'Discharge Summary',
        summary: 'For the end of an admission or episode of care, including transition planning and handoff details.',
        documentationSummary: 'Capture admission diagnosis, hospital course, procedures, discharge condition, medications, and follow-up.',
        titleSuggestion: 'Discharge Summary',
        icon: 'bx bx-log-out-circle',
        color: SLATE,
        fields: [
            { key: 'admission_diagnosis', label: 'Admission Diagnosis / Primary Problem', placeholder: 'What problem initiated the admission or episode?', rows: 3, required: true },
            { key: 'hospital_course', label: 'Hospital / Treatment Course', placeholder: 'Summarize key events, interventions, and progress during care.', rows: 4, required: true },
            { key: 'procedures_during_stay', label: 'Procedures / Key Interventions', placeholder: 'Important procedures or interventions performed.', rows: 3 },
            { key: 'discharge_condition', label: 'Condition at Discharge', placeholder: 'Functional, clinical, and stability status at discharge.', rows: 3, required: true },
            { key: 'discharge_medications', label: 'Discharge Medications', placeholder: 'Medication list, changes, and counselling provided.', rows: 3, required: true },
            { key: 'follow_up_arrangements', label: 'Follow-Up Arrangements', placeholder: 'Clinic review, labs, referrals, home care, and timing.', rows: 3, required: true },
            { key: 'discharge_instructions', label: 'Discharge Instructions', placeholder: 'Red flags, self-care instructions, activity, diet, and safety-net advice.', rows: 3, required: true },
        ],
    },
];

const getRecordTypeDefinition = (recordType: RecordTypeId | '') => (
    RECORD_TYPE_DEFINITIONS.find((definition) => definition.id === recordType) || null
);

const ModeCard: React.FC<{
    title: string;
    subtitle: string;
    icon: string;
    accent: string;
    onClick?: () => void;
    to?: string;
}> = ({ title, subtitle, icon, accent, onClick, to }) => {
    const content = (
        <div style={{ ...cardStyle, padding: 24, height: '100%', cursor: onClick || to ? 'pointer' : 'default', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }} onClick={onClick}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                <i className={icon} style={{ color: '#fff', fontSize: '1.55rem' }}></i>
            </div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', marginBottom: 8 }}>{title}</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.6 }}>{subtitle}</div>
        </div>
    );

    if (to) {
        return <Link to={to} className="text-decoration-none">{content}</Link>;
    }

    return content;
};

const StepRail: React.FC<{
    steps: { id: number; title: string; summary: string }[];
    active: number;
    completed: boolean[];
    onSelect: (id: number) => void;
}> = ({ steps, active, completed, onSelect }) => (
    <div style={{ ...cardStyle, padding: 18 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--uzima-shell-text-muted)', marginBottom: 10 }}>Workflow</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {steps.map((step, index) => {
                const isDone = completed[index];
                const isActive = active === step.id;
                return (
                    <button
                        key={step.id}
                        type="button"
                        onClick={() => onSelect(step.id)}
                        className="sc-btn sc-btn-ghost"
                        style={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            borderRadius: 14,
                            padding: '12px 14px',
                            background: isActive ? ACCENT_SOFT : isDone ? 'rgba(16,185,129,0.08)' : 'rgba(15,23,42,0.02)',
                            border: isActive ? `1px solid ${ACCENT}` : '1px solid transparent',
                            color: '#0f172a',
                        }}
                    >
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%' }}>
                            <div style={{ width: 28, height: 28, borderRadius: 10, background: isDone ? '#059669' : isActive ? ACCENT : 'rgba(100,116,139,0.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 800, flexShrink: 0 }}>
                                {isDone ? <i className="bx bx-check"></i> : step.id}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 2 }}>{step.title}</div>
                                <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.45 }}>{step.summary}</div>
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    </div>
);

const ModernEHRCreate: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const { recentlyCreatedPatient, setRecentlyCreatedPatient, clearRecentlyCreatedPatient } = usePatient();

    const [mode, setMode] = useState<CreateMode>('idle');
    const [patientStep, setPatientStep] = useState(1);
    const [recordStep, setRecordStep] = useState(1);
    const [patientForm, setPatientForm] = useState<PatientForm>(emptyPatientForm());
    const [recordForm, setRecordForm] = useState<RecordForm>(emptyRecordForm());
    const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);
    const [patientSearchOpen, setPatientSearchOpen] = useState(false);
    const [patientSearchTerm, setPatientSearchTerm] = useState('');
    const [patientResults, setPatientResults] = useState<PatientSummary[]>([]);
    const [allPatients, setAllPatients] = useState<PatientSummary[]>([]);
    const [searchingPatients, setSearchingPatients] = useState(false);
    const [loadingPatients, setLoadingPatients] = useState(false);
    const [privacyLevels, setPrivacyLevels] = useState<SelectOption[]>([]);
    const [recordStatuses, setRecordStatuses] = useState<SelectOption[]>([]);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [patientSubmitting, setPatientSubmitting] = useState(false);
    const [recordSubmitting, setRecordSubmitting] = useState(false);
    const [createdRecord, setCreatedRecord] = useState<{ id?: string | number; patientId: string; title: string } | null>(null);

    const apiUrl = (path: string) => `${String(baseUrl || '').replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

    useEffect(() => {
        document.title = 'Create Medical Record';
    }, []);

    useEffect(() => {
        const dirty = mode !== 'idle' || !!recordForm.title || !!recordForm.chiefComplaint || !!patientForm.first_name || !!selectedPatient;
        if (!dirty || createdRecord) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [mode, patientForm.first_name, recordForm.title, recordForm.chiefComplaint, selectedPatient, createdRecord]);

    useEffect(() => {
        if (recentlyCreatedPatient?.patient_id) {
            setSelectedPatient(recentlyCreatedPatient);
            setRecordForm(prev => ({ ...prev, admNo: String(recentlyCreatedPatient.patient_id || '') }));
            setMode('existing');
        }
    }, [recentlyCreatedPatient]);

    useEffect(() => {
        const loadConfiguration = async () => {
            try {
                setLoadingConfig(true);
                const [privacyResponse, statusResponse] = await Promise.all([
                    axios.get(apiUrl('/facilities/medical-records/privacy-levels')),
                    axios.get(apiUrl('/facilities/medical-records/statuses')),
                ]);

                if (privacyResponse.data?.success) {
                    setPrivacyLevels(privacyResponse.data.privacy_levels || []);
                    if (privacyResponse.data.default) {
                        setRecordForm(prev => ({ ...prev, privacyLevel: privacyResponse.data.default }));
                    }
                }

                if (statusResponse.data?.success) {
                    setRecordStatuses(statusResponse.data.statuses || []);
                    if (statusResponse.data.default) {
                        setRecordForm(prev => ({ ...prev, recordStatus: statusResponse.data.default }));
                    }
                }
            } catch {
                setPrivacyLevels([
                    { value: 'standard', label: 'Standard', description: 'Standard privacy level' },
                    { value: 'restricted', label: 'Restricted', description: 'Restricted access' },
                    { value: 'confidential', label: 'Confidential', description: 'Highly confidential' },
                ]);
                setRecordStatuses([
                    { value: 'draft', label: 'Draft', description: 'Draft record' },
                    { value: 'active', label: 'Active', description: 'Active record' },
                    { value: 'completed', label: 'Completed', description: 'Completed record' },
                ]);
            } finally {
                setLoadingConfig(false);
            }
        };

        loadConfiguration();
    }, [baseUrl]);

    const selectedRecordTypeDefinition = useMemo(() => getRecordTypeDefinition(recordForm.recordType), [recordForm.recordType]);

    const requiredTypeSpecificFieldsComplete = useMemo(() => {
        if (!selectedRecordTypeDefinition) {
            return false;
        }

        return selectedRecordTypeDefinition.fields
            .filter((field) => field.required)
            .every((field) => String(recordForm.typeSpecificData[field.key] || '').trim());
    }, [selectedRecordTypeDefinition, recordForm.typeSpecificData]);

    const recordSteps = [
        { id: 1, title: 'Patient & Visit Setup', summary: 'Choose patient, capture record title, privacy, and admin details.' },
        { id: 2, title: 'Record Type', summary: 'Choose the clinical note standard that matches the encounter and load its detailed form.' },
        { id: 3, title: selectedRecordTypeDefinition ? `${selectedRecordTypeDefinition.shortLabel} Details` : 'Clinical Details', summary: selectedRecordTypeDefinition ? selectedRecordTypeDefinition.documentationSummary : 'Capture the detailed clinical form for the selected note type.' },
        { id: 4, title: 'Exam & Assessment', summary: 'Document examination findings, review of systems, diagnosis, and plan.' },
        { id: 5, title: 'Vitals & Final Review', summary: 'Record vitals, confirm governance settings, and submit the chart.' },
    ];

    const patientSteps = [
        { id: 1, title: 'Identity', summary: 'Capture the patient’s name and date of birth.' },
        { id: 2, title: 'Contact & Demographics', summary: 'Add email and gender for account creation.' },
        { id: 3, title: 'Review', summary: 'Confirm details and create the patient account.' },
    ];

    const stepCompletion = useMemo(() => {
        const hasPatient = !!(selectedPatient?.patient_id || selectedPatient?.id || recordForm.admNo);
        return [
            hasPatient && !!recordForm.title,
            !!recordForm.recordType,
            !!recordForm.chiefComplaint && requiredTypeSpecificFieldsComplete,
            !!recordForm.physical && !!recordForm.assessment && !!recordForm.treatmentPlan,
            !!recordForm.temp && !!recordForm.heart && !!recordForm.bp,
        ];
    }, [selectedPatient, recordForm, requiredTypeSpecificFieldsComplete]);

    const patientStepCompletion = useMemo(() => ([
        !!patientForm.first_name && !!patientForm.last_name && !!patientForm.date_of_birth,
        !!patientForm.email && !!patientForm.gender,
        !!patientForm.first_name && !!patientForm.last_name && !!patientForm.date_of_birth && !!patientForm.email && !!patientForm.gender,
    ]), [patientForm]);

    const computedBmi = useMemo(() => {
        const heightCm = Number(recordForm.height);
        const weightKg = Number(recordForm.weight);
        if (!heightCm || !weightKg) return '';
        const heightM = heightCm / 100;
        if (!heightM) return '';
        return (weightKg / (heightM * heightM)).toFixed(1);
    }, [recordForm.height, recordForm.weight]);

    const updatePatientForm = <K extends keyof PatientForm>(field: K, value: PatientForm[K]) => {
        setPatientForm(prev => ({ ...prev, [field]: value }));
    };

    const updateRecordForm = <K extends keyof RecordForm>(field: K, value: RecordForm[K]) => {
        setRecordForm(prev => ({ ...prev, [field]: value }));
    };

    const updateTypeSpecificData = (field: string, value: string) => {
        setRecordForm(prev => ({
            ...prev,
            typeSpecificData: {
                ...prev.typeSpecificData,
                [field]: value,
            },
        }));
    };

    const selectRecordType = (recordType: RecordTypeId) => {
        const definition = getRecordTypeDefinition(recordType);
        setRecordForm((prev) => ({
            ...prev,
            recordType,
            title: !prev.title || RECORD_TYPE_DEFINITIONS.some((item) => item.titleSuggestion === prev.title)
                ? definition?.titleSuggestion || prev.title
                : prev.title,
        }));
    };

    const buildTypeSpecificNarrative = () => {
        if (!selectedRecordTypeDefinition) {
            return '';
        }

        const lines = selectedRecordTypeDefinition.fields
            .map((field) => {
                const value = String(recordForm.typeSpecificData[field.key] || '').trim();
                return value ? `${field.label}: ${value}` : '';
            })
            .filter(Boolean);

        if (!lines.length) {
            return '';
        }

        return [`Record Type: ${selectedRecordTypeDefinition.label}`, ...lines].join('\n');
    };

    const openPatientSearch = async () => {
        setPatientSearchOpen(true);
        setLoadingPatients(true);
        try {
            const clinicId = await localforage.getItem('clinic_id');
            const response = await axios.get(apiUrl(`/facilities/patients/list?clinic_id=${clinicId || 1}`));
            if (response.data?.status === 'success') {
                setAllPatients(response.data.data || []);
            } else {
                setAllPatients([]);
            }
        } catch {
            setAllPatients([]);
        } finally {
            setLoadingPatients(false);
        }
    };

    const searchPatients = async (term: string) => {
        setPatientSearchTerm(term);
        if (!term.trim()) {
            setPatientResults([]);
            return;
        }
        setSearchingPatients(true);
        try {
            const clinicId = await localforage.getItem('clinic_id');
            const response = await axios.get(apiUrl(`/facilities/patients/search?clinic_id=${clinicId || 1}&search=${encodeURIComponent(term)}`));
            if (response.data?.status === 'success') {
                setPatientResults(response.data.data || []);
            } else {
                setPatientResults([]);
            }
        } catch {
            setPatientResults([]);
        } finally {
            setSearchingPatients(false);
        }
    };

    const choosePatient = (patient: PatientSummary) => {
        setSelectedPatient(patient);
        setRecordForm(prev => ({ ...prev, admNo: String(patient.patient_id || patient.id || '') }));
        setMode('existing');
        setPatientSearchOpen(false);
        setPatientSearchTerm('');
        setPatientResults([]);
        if (recentlyCreatedPatient?.patient_id && recentlyCreatedPatient.patient_id !== patient.patient_id) {
            clearRecentlyCreatedPatient();
        }
    };

    const createPatient = async () => {
        if (!patientStepCompletion[patientStep - 1]) {
            await Swal.fire({ icon: 'warning', title: 'Complete required details', text: 'Please fill the required fields before continuing.' });
            return;
        }

        if (patientStep < patientSteps.length) {
            setPatientStep(prev => prev + 1);
            return;
        }

        setPatientSubmitting(true);
        try {
            const clinicId = await localforage.getItem('clinic_id');
            const staffId = await localforage.getItem('staff_id');

            // users/create-patient reads request.POST (form-encoded) and the field is
            // named `dob`, not `date_of_birth` — match the shape PatientRegister.tsx uses.
            const formData = new FormData();
            formData.append('first_name', patientForm.first_name);
            formData.append('middle_name', patientForm.middle_name);
            formData.append('last_name', patientForm.last_name);
            formData.append('other_name', patientForm.other_name);
            formData.append('email', patientForm.email);
            formData.append('dob', patientForm.date_of_birth);
            formData.append('gender', patientForm.gender);
            formData.append('staff_id', String(staffId || 1));
            formData.append('clinic_id', String(clinicId || 1));

            const response = await axios.post(apiUrl('/users/create-patient'), formData);

            const { status, data, message } = response.data || {};
            const admissionNumber = data?.admission_number;
            if (status === 'success' && admissionNumber) {
                // The endpoint only echoes back the new patient_id, so build the summary
                // from the form we already have instead of round-tripping the full record.
                const createdPatient: PatientSummary = {
                    patient_id: admissionNumber,
                    id: admissionNumber,
                    first_name: patientForm.first_name,
                    last_name: patientForm.last_name,
                    email: patientForm.email,
                    gender: patientForm.gender,
                    date_of_birth: patientForm.date_of_birth,
                };
                setRecentlyCreatedPatient(createdPatient);
                setSelectedPatient(createdPatient);
                setRecordForm(prev => ({ ...prev, admNo: admissionNumber }));
                setPatientForm(emptyPatientForm());
                setPatientStep(1);
                setMode('existing');
                await Swal.fire({ icon: 'success', title: 'Patient created', text: `Patient ID: ${admissionNumber}` });
            } else {
                await Swal.fire({ icon: 'error', title: 'Could not create patient', text: message || 'The patient account could not be created.' });
            }
        } catch (error: any) {
            await Swal.fire({ icon: 'error', title: 'Could not create patient', text: error?.response?.data?.message || 'The patient account could not be created.' });
        } finally {
            setPatientSubmitting(false);
        }
    };

    const handleRecordStepAdvance = async () => {
        if (!stepCompletion[recordStep - 1]) {
            await Swal.fire({ icon: 'warning', title: 'Complete required details', text: 'Please fill the required fields before continuing.' });
            return;
        }
        if (recordStep < recordSteps.length) {
            setRecordStep(prev => prev + 1);
        } else {
            await createMedicalRecord();
        }
    };

    const createMedicalRecord = async () => {
        if (!stepCompletion.every(Boolean)) {
            await Swal.fire({ icon: 'warning', title: 'Complete required details', text: 'Please finish the required sections before submitting.' });
            return;
        }

        const clinicId = await localforage.getItem('clinic_id');
        const staffId = await localforage.getItem('staff_id');
        const patientId = recordForm.admNo || String(selectedPatient?.patient_id || selectedPatient?.id || '');
        const typeSpecificNarrative = buildTypeSpecificNarrative();
        const compiledProviderNotes = [typeSpecificNarrative, recordForm.providerNotes].filter(Boolean).join('\n\n');

        if (!patientId || !recordForm.title || !recordForm.recordType || !recordForm.chiefComplaint || !recordForm.physical || !recordForm.assessment || !recordForm.treatmentPlan || !recordForm.temp || !recordForm.heart || !recordForm.bp) {
            await Swal.fire({ icon: 'warning', title: 'Missing required details', text: 'Patient, title, record type, core clinical details, examination, assessment, plan, and vital signs are required.' });
            return;
        }

        setRecordSubmitting(true);
        try {
            const payload = {
                staff_id: staffId || 1,
                clinic_id: clinicId || 1,
                patient_id: patientId,
                name: recordForm.title,
                status: recordForm.recordStatus,
                privacy_level: recordForm.privacyLevel,
                is_confidential: recordForm.isConfidential,
                requires_follow_up: recordForm.requiresFollowUp,
                emergency_contact_name: recordForm.emergencyContactName,
                emergency_contact_phone: recordForm.emergencyContactPhone,
                emergency_contact_relationship: recordForm.emergencyContactRelationship,
                insurance_information: recordForm.insuranceInformation,
                chief_complaint: recordForm.chiefComplaint,
                history_present_illness: recordForm.historyPresentIllness,
                hpi_onset: recordForm.hpiOnset,
                hpi_location: recordForm.hpiLocation,
                hpi_duration: recordForm.hpiDuration,
                hpi_characteristics: recordForm.hpiCharacteristics,
                hpi_aggravating_factors: recordForm.hpiAggravatingFactors,
                hpi_alleviating_factors: recordForm.hpiAlleviatingFactors,
                hpi_related_symptoms: recordForm.hpiRelatedSymptoms,
                hpi_treatments_tried: recordForm.hpiTreatmentsTried,
                medical_history: recordForm.medicalHistory,
                allergies: recordForm.allergies,
                current_medications: recordForm.currentMedications,
                previous_surgeries: recordForm.previousSurgeries,
                previous_hospitalizations: recordForm.previousHospitalizations,
                past_injuries: recordForm.pastInjuries,
                family_history: recordForm.familyHistory,
                social_history: recordForm.socialHistory,
                occupation: recordForm.occupation,
                smoking_status: recordForm.smokingStatus,
                alcohol_use: recordForm.alcoholUse,
                drug_use: recordForm.drugUse,
                exercise_habits: recordForm.exerciseHabits,
                diet: recordForm.diet,
                living_situation: recordForm.livingSituation,
                review_of_systems: recordForm.reviewOfSystems,
                ros_constitutional: '',
                ros_eyes: '',
                ros_ears_nose_throat: '',
                ros_cardiovascular: '',
                ros_respiratory: '',
                ros_gastrointestinal: '',
                ros_genitourinary: '',
                ros_musculoskeletal: '',
                ros_skin: '',
                ros_neurological: '',
                ros_psychiatric: '',
                ros_endocrine: '',
                ros_hematologic: '',
                ros_allergic: '',
                physical_examination: recordForm.physical,
                general_appearance: recordForm.generalAppearance,
                assessment: recordForm.assessment,
                plan: recordForm.treatmentPlan,
                patient_education: recordForm.patientEducation,
                follow_up_instructions: recordForm.followUpInstructions,
                diagnosis_code: recordForm.diagnosisCode,
                diagnosis_description: recordForm.diagnosisDescription,
                provider_notes: compiledProviderNotes,
                provider_signature: recordForm.providerSignature,
                advance_directives: recordForm.advanceDirectives,
                immunization_history: recordForm.immunizationHistory,
                growth_charts: recordForm.growthCharts,
                mental_health_assessment: recordForm.mentalHealthAssessment,
                consent_forms: recordForm.consentForms,
                temperature: recordForm.temp,
                heart_rate: recordForm.heart,
                blood_pressure: recordForm.bp,
                blood_glucose: recordForm.bg,
                bmi: computedBmi,
                height: recordForm.height,
                weight: recordForm.weight,
                respiratory_rate: recordForm.resp,
                oxygen_saturation: recordForm.oxygen,
                vital_notes: recordForm.vitalNotes,
                pain_score: recordForm.painScore,
                lab_test: recordForm.labTest,
                lab_result: recordForm.labResult,
                lab_reference_range: recordForm.labRef,
            };

            const response = await axios.post(apiUrl('/ehr/api/v1/medical-records/'), payload, { headers: { 'Content-Type': 'application/json' } });
            const { status, data, message } = response.data || {};
            if (status === 'success') {
                setCreatedRecord({ id: data?.id, patientId, title: recordForm.title });
                setRecordForm(emptyRecordForm());
                setRecordStep(1);
                await Swal.fire({ icon: 'success', title: 'Medical record created', text: `The medical record for patient ${patientId} has been created successfully.` });
            } else {
                await Swal.fire({ icon: 'error', title: 'Could not create record', text: message || 'The medical record could not be created.' });
            }
        } catch (error: any) {
            await Swal.fire({ icon: 'error', title: 'Could not create record', text: error?.response?.data?.message || error?.message || 'The medical record could not be created.' });
        } finally {
            setRecordSubmitting(false);
        }
    };

    const resetWorkspace = () => {
        setMode('idle');
        setPatientStep(1);
        setRecordStep(1);
        setPatientForm(emptyPatientForm());
        setRecordForm(emptyRecordForm());
        setSelectedPatient(null);
        setCreatedRecord(null);
        clearRecentlyCreatedPatient();
    };

    const patientDisplayId = String(selectedPatient?.patient_id || selectedPatient?.id || recordForm.admNo || '');
    const patientDisplayName = [selectedPatient?.first_name, selectedPatient?.last_name].filter(Boolean).join(' ');
    const chiefComplaintLabel = selectedRecordTypeDefinition?.id === 'discharge_summary'
        ? 'Primary Reason for Admission / Episode *'
        : selectedRecordTypeDefinition?.id === 'procedure_note'
            ? 'Primary Procedure Concern / Indication *'
            : 'Chief Complaint *';
    const chiefComplaintPlaceholder = selectedRecordTypeDefinition?.id === 'discharge_summary'
        ? 'Summarize the core diagnosis or reason this episode of care began.'
        : selectedRecordTypeDefinition?.id === 'procedure_note'
            ? 'Document the problem or indication that led to the procedure note.'
            : selectedRecordTypeDefinition?.id === 'progress_note'
                ? 'What is the main issue being reviewed in this follow-up note?'
                : 'What is the patient’s primary concern today?';

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <EHRNav />
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-plus-medical me-2" style={{ color: ACCENT }}></i>Modern EHR Create</h1>
                                <p>Create a patient and capture a complete record in a guided, clinical workflow.</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ padding: '6px 14px', borderRadius: 999, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.74rem', fontWeight: 700 }}>
                                    <i className="bx bx-shield-quarter me-1"></i>Structured EHR Intake
                                </span>
                                {mode !== 'idle' && (
                                    <button className="sc-btn sc-btn-ghost" onClick={resetWorkspace}>
                                        <i className="bx bx-reset me-1"></i>Reset
                                    </button>
                                )}
                            </div>
                        </div>

                        {createdRecord ? (
                            <div style={{ ...cardStyle, padding: 28 }}>
                                <div className="row g-4 align-items-center">
                                    <div className="col-lg-8">
                                        <div style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(16,185,129,0.12)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                                            <i className="bx bx-check-shield" style={{ fontSize: '1.9rem' }}></i>
                                        </div>
                                        <h3 style={{ marginBottom: 10 }}>Medical record created successfully</h3>
                                        <p style={{ color: 'var(--uzima-shell-text-muted)', marginBottom: 18 }}>
                                            The new EHR entry has been created for patient <b>{createdRecord.patientId}</b> with title <b>{createdRecord.title}</b>.
                                        </p>
                                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                            <button className="sc-btn sc-btn-primary" onClick={() => setCreatedRecord(null)}>
                                                <i className="bx bx-plus me-1"></i>Create Another Record
                                            </button>
                                            <button className="sc-btn sc-btn-ghost" onClick={() => navigate('/institution/ehr')}>
                                                <i className="bx bx-arrow-back me-1"></i>Back to EHR Dashboard
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div style={{ ...cardStyle, background: ACCENT_SOFT, padding: 20 }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.35px', color: 'var(--uzima-shell-text-muted)', marginBottom: 12 }}>Record Summary</div>
                                            <div style={{ display: 'grid', gap: 12 }}>
                                                <div>
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Record ID</div>
                                                    <div style={{ fontWeight: 700 }}>{createdRecord.id || 'Pending server reference'}</div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Patient</div>
                                                    <div style={{ fontWeight: 700 }}>{createdRecord.patientId}</div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Created</div>
                                                    <div style={{ fontWeight: 700 }}>{new Date().toLocaleString()}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : mode === 'idle' ? (
                            <div className="row g-4">
                                <div className="col-md-4">
                                    <ModeCard
                                        title="Create New Patient"
                                        subtitle="Register a brand new patient first, then move directly into medical record capture without losing context."
                                        icon="bx bx-user-plus"
                                        accent={ACCENT}
                                        onClick={() => setMode('new')}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <ModeCard
                                        title="Use Existing Patient"
                                        subtitle="Search the patient roster, choose the right chart, and open a structured record workflow immediately."
                                        icon="bx bx-user-check"
                                        accent={BLUE}
                                        onClick={openPatientSearch}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <ModeCard
                                        title="Back to EHR Dashboard"
                                        subtitle="Return to search, browse, share, and activity views for your institution’s medical records."
                                        icon="bx bx-arrow-back"
                                        accent={SLATE}
                                        to="/institution/ehr"
                                    />
                                </div>
                            </div>
                        ) : mode === 'new' ? (
                            <div className="row g-4">
                                <div className="col-xl-3">
                                    <StepRail steps={patientSteps} active={patientStep} completed={patientStepCompletion} onSelect={setPatientStep} />
                                </div>
                                <div className="col-xl-9">
                                    <div style={{ ...cardStyle, padding: 22 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap' }}>
                                            <div>
                                                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.35px', marginBottom: 4 }}>New Patient Registration</div>
                                                <h4 style={{ marginBottom: 6 }}>{patientSteps[patientStep - 1].title}</h4>
                                                <p style={{ marginBottom: 0, color: 'var(--uzima-shell-text-muted)' }}>{patientSteps[patientStep - 1].summary}</p>
                                            </div>
                                            <div style={{ padding: '8px 12px', borderRadius: 999, background: ACCENT_SOFT, color: ACCENT, fontWeight: 700, fontSize: '0.74rem' }}>
                                                Step {patientStep} / {patientSteps.length}
                                            </div>
                                        </div>

                                        {patientStep === 1 && (
                                            <div className="row g-4">
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>First Name *</label>
                                                    <input className="sc-input" value={patientForm.first_name} onChange={(e) => updatePatientForm('first_name', e.target.value)} placeholder="Enter first name" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Middle Name</label>
                                                    <input className="sc-input" value={patientForm.middle_name} onChange={(e) => updatePatientForm('middle_name', e.target.value)} placeholder="Optional middle name" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Last Name *</label>
                                                    <input className="sc-input" value={patientForm.last_name} onChange={(e) => updatePatientForm('last_name', e.target.value)} placeholder="Enter last name" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Other Name</label>
                                                    <input className="sc-input" value={patientForm.other_name} onChange={(e) => updatePatientForm('other_name', e.target.value)} placeholder="Optional other name" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Date of Birth *</label>
                                                    <input className="sc-input" type="date" value={patientForm.date_of_birth} onChange={(e) => updatePatientForm('date_of_birth', e.target.value)} />
                                                </div>
                                            </div>
                                        )}

                                        {patientStep === 2 && (
                                            <div className="row g-4">
                                                <div className="col-md-7">
                                                    <label style={labelStyle}>Email Address *</label>
                                                    <input className="sc-input" type="email" value={patientForm.email} onChange={(e) => updatePatientForm('email', e.target.value)} placeholder="patient@example.com" />
                                                </div>
                                                <div className="col-md-5">
                                                    <label style={labelStyle}>Gender *</label>
                                                    <select className="sc-input" value={patientForm.gender} onChange={(e) => updatePatientForm('gender', e.target.value)}>
                                                        <option value="">Select gender</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="col-12">
                                                    <div style={{ ...cardStyle, background: 'rgba(15,118,110,0.04)', padding: 16 }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Why this matters</div>
                                                        <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem', lineHeight: 1.6 }}>
                                                            These demographics support chart creation, patient lookup, and safer communication after the visit. The medical record workflow will continue immediately after account creation.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {patientStep === 3 && (
                                            <div className="row g-4">
                                                <div className="col-lg-7">
                                                    <div style={{ ...cardStyle, background: 'rgba(15,118,110,0.04)', padding: 18 }}>
                                                        <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: 14 }}>Patient Summary</div>
                                                        <div className="row g-3">
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Name</div><div style={{ fontWeight: 700 }}>{[patientForm.first_name, patientForm.middle_name, patientForm.last_name, patientForm.other_name].filter(Boolean).join(' ') || '—'}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Date of Birth</div><div style={{ fontWeight: 700 }}>{patientForm.date_of_birth || '—'}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Gender</div><div style={{ fontWeight: 700 }}>{patientForm.gender || '—'}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Email</div><div style={{ fontWeight: 700 }}>{patientForm.email || '—'}</div></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-5">
                                                    <div style={{ ...cardStyle, background: BLUE_SOFT, padding: 18, height: '100%' }}>
                                                        <div style={{ fontWeight: 800, marginBottom: 8 }}>Next after creation</div>
                                                        <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem', lineHeight: 1.65 }}>
                                                            Once you create this patient, the chart workflow will automatically load with the patient selected so you can continue with the medical record without re-searching.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
                                            <button className="sc-btn sc-btn-ghost" onClick={() => patientStep === 1 ? setMode('idle') : setPatientStep(prev => prev - 1)}>
                                                <i className="bx bx-arrow-back me-1"></i>{patientStep === 1 ? 'Back to options' : 'Previous'}
                                            </button>
                                            <button className="sc-btn sc-btn-primary" onClick={createPatient} disabled={patientSubmitting}>
                                                {patientSubmitting
                                                    ? <><i className="bx bx-loader-alt bx-spin me-1"></i>Creating patient...</>
                                                    : patientStep === patientSteps.length
                                                        ? <><i className="bx bx-check me-1"></i>Create patient and continue</>
                                                        : <><i className="bx bx-arrow-right me-1"></i>Continue</>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="row g-4">
                                <div className="col-xl-3">
                                    <div style={{ display: 'grid', gap: 16 }}>
                                        <StepRail steps={recordSteps} active={recordStep} completed={stepCompletion} onSelect={setRecordStep} />
                                        <div style={{ ...cardStyle, padding: 16 }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.35px', color: 'var(--uzima-shell-text-muted)', marginBottom: 10 }}>Selected Patient</div>
                                            {selectedPatient ? (
                                                <>
                                                    <div style={{ fontWeight: 800, marginBottom: 6 }}>{patientDisplayName || patientDisplayId}</div>
                                                    <div style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>ID: {patientDisplayId || '—'}</div>
                                                    {selectedPatient.email && <div style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>{selectedPatient.email}</div>}
                                                    {selectedPatient.gender && <div style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>{selectedPatient.gender}</div>}
                                                </>
                                            ) : (
                                                <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem' }}>No patient selected yet.</div>
                                            )}
                                            {selectedRecordTypeDefinition && (
                                                <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 12, background: ACCENT_SOFT, color: '#0f172a' }}>
                                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.35px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>Selected Record Type</div>
                                                    <div style={{ fontWeight: 700 }}>{selectedRecordTypeDefinition.label}</div>
                                                </div>
                                            )}
                                            <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                                                <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={openPatientSearch}><i className="bx bx-search me-1"></i>Change patient</button>
                                                {recentlyCreatedPatient?.patient_id && (
                                                    <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => choosePatient(recentlyCreatedPatient)}>
                                                        <i className="bx bx-user-check me-1"></i>Use recently created patient
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-9">
                                    <div style={{ ...cardStyle, padding: 22, marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                            <div>
                                                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.35px', marginBottom: 4 }}>Medical Record Workflow</div>
                                                <h4 style={{ marginBottom: 6 }}>{recordSteps[recordStep - 1].title}</h4>
                                                <p style={{ marginBottom: 0, color: 'var(--uzima-shell-text-muted)' }}>{recordSteps[recordStep - 1].summary}</p>
                                            </div>
                                            <div style={{ padding: '8px 12px', borderRadius: 999, background: BLUE_SOFT, color: BLUE, fontWeight: 700, fontSize: '0.74rem' }}>
                                                Step {recordStep} / {recordSteps.length}
                                            </div>
                                        </div>
                                    </div>

                                    {recordStep === 1 && (
                                        <div style={{ ...cardStyle, padding: 22 }}>
                                            <div className="row g-4">
                                                <div className="col-lg-7">
                                                    <label style={labelStyle}>Patient ID *</label>
                                                    <div className="d-flex gap-2">
                                                        <input className="sc-input" value={recordForm.admNo} onChange={(e) => updateRecordForm('admNo', e.target.value)} placeholder="Patient admission number or patient ID" />
                                                        <button className="sc-btn sc-btn-ghost" onClick={openPatientSearch}><i className="bx bx-search"></i></button>
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--uzima-shell-text-muted)', marginTop: 6 }}>Search the patient list or paste the known patient ID.</div>
                                                </div>
                                                <div className="col-lg-5">
                                                    <label style={labelStyle}>Record Title *</label>
                                                    <input className="sc-input" value={recordForm.title} onChange={(e) => updateRecordForm('title', e.target.value)} placeholder="e.g. Outpatient consultation note" />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Record Status</label>
                                                    <select className="sc-input" value={recordForm.recordStatus} onChange={(e) => updateRecordForm('recordStatus', e.target.value)} disabled={loadingConfig}>
                                                        {(recordStatuses.length ? recordStatuses : [{ value: 'draft', label: 'Draft' }]).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Privacy Level</label>
                                                    <select className="sc-input" value={recordForm.privacyLevel} onChange={(e) => updateRecordForm('privacyLevel', e.target.value)} disabled={loadingConfig}>
                                                        {(privacyLevels.length ? privacyLevels : [{ value: 'standard', label: 'Standard' }]).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Emergency Contact Name</label>
                                                    <input className="sc-input" value={recordForm.emergencyContactName} onChange={(e) => updateRecordForm('emergencyContactName', e.target.value)} placeholder="Full name" />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Emergency Contact Phone</label>
                                                    <input className="sc-input" value={recordForm.emergencyContactPhone} onChange={(e) => updateRecordForm('emergencyContactPhone', e.target.value)} placeholder="Phone number" />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Relationship</label>
                                                    <input className="sc-input" value={recordForm.emergencyContactRelationship} onChange={(e) => updateRecordForm('emergencyContactRelationship', e.target.value)} placeholder="Parent, spouse, guardian" />
                                                </div>
                                                <div className="col-12">
                                                    <label style={labelStyle}>Insurance Information</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.insuranceInformation} onChange={(e) => updateRecordForm('insuranceInformation', e.target.value)} placeholder="Provider, policy number, payer notes" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="d-flex align-items-center gap-2" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                                        <input type="checkbox" checked={recordForm.isConfidential} onChange={(e) => updateRecordForm('isConfidential', e.target.checked)} />
                                                        Mark as confidential
                                                    </label>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="d-flex align-items-center gap-2" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                                        <input type="checkbox" checked={recordForm.requiresFollowUp} onChange={(e) => updateRecordForm('requiresFollowUp', e.target.checked)} />
                                                        Requires follow-up
                                                    </label>
                                                </div>

                                                <div className="col-12">
                                                    <div style={{ ...cardStyle, background: SLATE_SOFT, padding: 16 }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 6 }}>What comes next</div>
                                                        <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem', lineHeight: 1.65 }}>
                                                            Next you will choose the clinical note category so the workflow can open a tailored documentation form based on the kind of record you are creating.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {recordStep === 2 && (
                                        <div style={{ ...cardStyle, padding: 22 }}>
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <div style={{ ...cardStyle, background: SLATE_SOFT, padding: 16 }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Standards-based note categories</div>
                                                        <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem', lineHeight: 1.65 }}>
                                                            Choose the kind of medical record you are creating so the system can load the right detailed form. The options below reflect widely used clinical note categories such as outpatient consultation, H&amp;P, progress, emergency, procedure, and discharge documentation.
                                                        </div>
                                                    </div>
                                                </div>

                                                {RECORD_TYPE_DEFINITIONS.map((definition) => {
                                                    const selected = recordForm.recordType === definition.id;

                                                    return (
                                                        <div className="col-lg-6" key={definition.id}>
                                                            <button
                                                                type="button"
                                                                className="sc-btn sc-btn-ghost"
                                                                onClick={() => selectRecordType(definition.id)}
                                                                style={{
                                                                    ...cardStyle,
                                                                    width: '100%',
                                                                    padding: 18,
                                                                    justifyContent: 'flex-start',
                                                                    textAlign: 'left',
                                                                    border: selected ? `1px solid ${definition.color}` : '1px solid rgba(15,23,42,0.06)',
                                                                    background: selected ? 'rgba(15,23,42,0.02)' : '#fff',
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', width: '100%' }}>
                                                                    <div style={{ width: 48, height: 48, borderRadius: 14, background: definition.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                        <i className={definition.icon} style={{ fontSize: '1.35rem' }}></i>
                                                                    </div>
                                                                    <div style={{ minWidth: 0 }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                                                                            <div style={{ fontWeight: 800, color: '#0f172a' }}>{definition.label}</div>
                                                                            {selected && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: definition.color }}>Selected</span>}
                                                                        </div>
                                                                        <div style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.6, marginBottom: 10 }}>{definition.summary}</div>
                                                                        <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.55 }}>{definition.documentationSummary}</div>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        </div>
                                                    );
                                                })}

                                                {selectedRecordTypeDefinition && (
                                                    <div className="col-12">
                                                        <div style={{ ...cardStyle, background: ACCENT_SOFT, padding: 18 }}>
                                                            <div style={{ fontWeight: 800, marginBottom: 6 }}>{selectedRecordTypeDefinition.label} selected</div>
                                                            <div style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.65 }}>
                                                                The next step will load the detailed fields for this record type and keep the documentation aligned with that note structure.
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {recordStep === 3 && (
                                        <div style={{ ...cardStyle, padding: 22 }}>
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <div style={{ ...cardStyle, background: selectedRecordTypeDefinition ? `${selectedRecordTypeDefinition.color}14` : ACCENT_SOFT, padding: 16 }}>
                                                        <div style={{ fontWeight: 800, marginBottom: 6 }}>{selectedRecordTypeDefinition?.label || 'Select a record type first'}</div>
                                                        <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem', lineHeight: 1.65 }}>
                                                            {selectedRecordTypeDefinition?.documentationSummary || 'Choose a record type on the previous step to load the correct documentation fields.'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>{chiefComplaintLabel}</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.chiefComplaint} onChange={(e) => updateRecordForm('chiefComplaint', e.target.value)} placeholder={chiefComplaintPlaceholder} style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>History of Present Illness</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.historyPresentIllness} onChange={(e) => updateRecordForm('historyPresentIllness', e.target.value)} placeholder="Narrative description of the presenting illness or episode" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Onset</label>
                                                    <input className="sc-input" value={recordForm.hpiOnset} onChange={(e) => updateRecordForm('hpiOnset', e.target.value)} placeholder="When did it start?" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Duration</label>
                                                    <input className="sc-input" value={recordForm.hpiDuration} onChange={(e) => updateRecordForm('hpiDuration', e.target.value)} placeholder="How long has it been present?" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Location / Context</label>
                                                    <input className="sc-input" value={recordForm.hpiLocation} onChange={(e) => updateRecordForm('hpiLocation', e.target.value)} placeholder="Body area, context, or site" />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Medical History</label>
                                                    <textarea className="sc-input" rows={4} value={recordForm.medicalHistory} onChange={(e) => updateRecordForm('medicalHistory', e.target.value)} placeholder="Past illnesses, chronic conditions, and significant history" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Current Medications</label>
                                                    <textarea className="sc-input" rows={4} value={recordForm.currentMedications} onChange={(e) => updateRecordForm('currentMedications', e.target.value)} placeholder="Medication list and dosages" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Allergies</label>
                                                    <textarea className="sc-input" rows={4} value={recordForm.allergies} onChange={(e) => updateRecordForm('allergies', e.target.value)} placeholder="Drug, food, or environmental allergies" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Family History</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.familyHistory} onChange={(e) => updateRecordForm('familyHistory', e.target.value)} placeholder="Relevant family conditions" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Social History</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.socialHistory} onChange={(e) => updateRecordForm('socialHistory', e.target.value)} placeholder="Social context, support system, and living circumstances" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-4">
                                                    <label style={labelStyle}>Occupation</label>
                                                    <input className="sc-input" value={recordForm.occupation} onChange={(e) => updateRecordForm('occupation', e.target.value)} placeholder="Occupation" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Smoking Status</label>
                                                    <input className="sc-input" value={recordForm.smokingStatus} onChange={(e) => updateRecordForm('smokingStatus', e.target.value)} placeholder="Never, former, current" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Alcohol Use</label>
                                                    <input className="sc-input" value={recordForm.alcoholUse} onChange={(e) => updateRecordForm('alcoholUse', e.target.value)} placeholder="Frequency or none" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Drug Use</label>
                                                    <input className="sc-input" value={recordForm.drugUse} onChange={(e) => updateRecordForm('drugUse', e.target.value)} placeholder="Relevant use history" />
                                                </div>

                                                {selectedRecordTypeDefinition?.fields.map((field) => (
                                                    <div className="col-lg-6" key={field.key}>
                                                        <label style={labelStyle}>{field.label}{field.required ? ' *' : ''}</label>
                                                        <textarea
                                                            className="sc-input"
                                                            rows={field.rows || 3}
                                                            value={recordForm.typeSpecificData[field.key] || ''}
                                                            onChange={(e) => updateTypeSpecificData(field.key, e.target.value)}
                                                            placeholder={field.placeholder}
                                                            style={{ resize: 'vertical' }}
                                                        />
                                                    </div>
                                                ))}

                                                {!selectedRecordTypeDefinition && (
                                                    <div className="col-12">
                                                        <div style={{ ...cardStyle, background: SLATE_SOFT, padding: 16 }}>
                                                            <div style={{ fontWeight: 700, marginBottom: 6 }}>Select a record type first</div>
                                                            <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.84rem', lineHeight: 1.65 }}>
                                                                Go back one step and choose the clinical note type before filling out the detailed documentation form.
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="col-12">
                                                    <label style={labelStyle}>Provider Notes</label>
                                                    <textarea className="sc-input" rows={4} value={recordForm.providerNotes} onChange={(e) => updateRecordForm('providerNotes', e.target.value)} placeholder="Additional provider notes that are not already captured in the structured template" style={{ resize: 'vertical' }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {recordStep === 4 && (
                                        <div style={{ ...cardStyle, padding: 22 }}>
                                            <div className="row g-4">
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Review of Systems</label>
                                                    <textarea className="sc-input" rows={5} value={recordForm.reviewOfSystems} onChange={(e) => updateRecordForm('reviewOfSystems', e.target.value)} placeholder="System-based symptom review" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>General Appearance</label>
                                                    <textarea className="sc-input" rows={5} value={recordForm.generalAppearance} onChange={(e) => updateRecordForm('generalAppearance', e.target.value)} placeholder="Overall appearance and clinical impression" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-12">
                                                    <label style={labelStyle}>Physical Examination *</label>
                                                    <textarea className="sc-input" rows={5} value={recordForm.physical} onChange={(e) => updateRecordForm('physical', e.target.value)} placeholder="Structured physical exam findings" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Assessment *</label>
                                                    <textarea className="sc-input" rows={4} value={recordForm.assessment} onChange={(e) => updateRecordForm('assessment', e.target.value)} placeholder="Clinical assessment and impression" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Treatment Plan *</label>
                                                    <textarea className="sc-input" rows={4} value={recordForm.treatmentPlan} onChange={(e) => updateRecordForm('treatmentPlan', e.target.value)} placeholder="Management plan, orders, and recommendations" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label style={labelStyle}>Diagnosis Code</label>
                                                    <input className="sc-input" value={recordForm.diagnosisCode} onChange={(e) => updateRecordForm('diagnosisCode', e.target.value)} placeholder="ICD-10 or internal code" />
                                                </div>
                                                <div className="col-md-8">
                                                    <label style={labelStyle}>Diagnosis Description</label>
                                                    <input className="sc-input" value={recordForm.diagnosisDescription} onChange={(e) => updateRecordForm('diagnosisDescription', e.target.value)} placeholder="Diagnosis description" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Patient Education</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.patientEducation} onChange={(e) => updateRecordForm('patientEducation', e.target.value)} placeholder="Counselling and patient education delivered" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Follow-up Instructions</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.followUpInstructions} onChange={(e) => updateRecordForm('followUpInstructions', e.target.value)} placeholder="When and how to return or escalate" style={{ resize: 'vertical' }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {recordStep === 5 && (
                                        <div style={{ ...cardStyle, padding: 22 }}>
                                            <div className="row g-4">
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Temperature *</label>
                                                    <input className="sc-input" type="number" step="0.1" value={recordForm.temp} onChange={(e) => updateRecordForm('temp', e.target.value)} placeholder="36.5" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Heart Rate *</label>
                                                    <input className="sc-input" type="number" value={recordForm.heart} onChange={(e) => updateRecordForm('heart', e.target.value)} placeholder="80" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Blood Pressure *</label>
                                                    <input className="sc-input" value={recordForm.bp} onChange={(e) => updateRecordForm('bp', e.target.value)} placeholder="120/80" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Pain Score</label>
                                                    <input className="sc-input" type="number" min="0" max="10" value={recordForm.painScore} onChange={(e) => updateRecordForm('painScore', e.target.value)} placeholder="0-10" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Weight (kg)</label>
                                                    <input className="sc-input" type="number" step="0.1" value={recordForm.weight} onChange={(e) => updateRecordForm('weight', e.target.value)} placeholder="70" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Height (cm)</label>
                                                    <input className="sc-input" type="number" step="0.1" value={recordForm.height} onChange={(e) => updateRecordForm('height', e.target.value)} placeholder="170" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Respiratory Rate</label>
                                                    <input className="sc-input" value={recordForm.resp} onChange={(e) => updateRecordForm('resp', e.target.value)} placeholder="18" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label style={labelStyle}>Oxygen Saturation</label>
                                                    <input className="sc-input" value={recordForm.oxygen} onChange={(e) => updateRecordForm('oxygen', e.target.value)} placeholder="98%" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Blood Glucose</label>
                                                    <input className="sc-input" value={recordForm.bg} onChange={(e) => updateRecordForm('bg', e.target.value)} placeholder="Optional" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label style={labelStyle}>Calculated BMI</label>
                                                    <input className="sc-input" value={computedBmi} readOnly placeholder="Calculated automatically" />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Vital Notes</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.vitalNotes} onChange={(e) => updateRecordForm('vitalNotes', e.target.value)} placeholder="Interpretation or concerns from vitals" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label style={labelStyle}>Mental Health / Consent Notes</label>
                                                    <textarea className="sc-input" rows={3} value={recordForm.mentalHealthAssessment} onChange={(e) => updateRecordForm('mentalHealthAssessment', e.target.value)} placeholder="Mental health assessment, consent, or other final governance notes" style={{ resize: 'vertical' }} />
                                                </div>
                                                <div className="col-12">
                                                    <div style={{ ...cardStyle, background: ACCENT_SOFT, padding: 18 }}>
                                                        <div className="row g-3">
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Patient</div><div style={{ fontWeight: 800 }}>{patientDisplayName || patientDisplayId || 'Not selected'}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Record Title</div><div style={{ fontWeight: 800 }}>{recordForm.title || 'Untitled record'}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Record Type</div><div style={{ fontWeight: 800 }}>{selectedRecordTypeDefinition?.label || 'Not selected'}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Status</div><div style={{ fontWeight: 800 }}>{recordForm.recordStatus}</div></div>
                                                            <div className="col-md-6"><div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Privacy</div><div style={{ fontWeight: 800 }}>{recordForm.privacyLevel}</div></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
                                        <button className="sc-btn sc-btn-ghost" onClick={() => recordStep === 1 ? setMode('idle') : setRecordStep(prev => prev - 1)}>
                                            <i className="bx bx-arrow-back me-1"></i>{recordStep === 1 ? 'Back to options' : 'Previous'}
                                        </button>
                                        <button className="sc-btn sc-btn-primary" onClick={handleRecordStepAdvance} disabled={recordSubmitting}>
                                            {recordSubmitting
                                                ? <><i className="bx bx-loader-alt bx-spin me-1"></i>Creating record...</>
                                                : recordStep === recordSteps.length
                                                    ? <><i className="bx bx-check me-1"></i>Create medical record</>
                                                    : <><i className="bx bx-arrow-right me-1"></i>Continue</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {patientSearchOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
                    <div style={{ ...cardStyle, width: 'min(980px, 100%)', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(15,23,42,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                            <div>
                                <div style={{ fontWeight: 800, fontSize: '1rem' }}>Select patient</div>
                                <div style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.82rem' }}>Search by name, email, or patient ID.</div>
                            </div>
                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setPatientSearchOpen(false)}>
                                <i className="bx bx-x"></i>
                            </button>
                        </div>
                        <div style={{ padding: 20, overflowY: 'auto' }}>
                            <div className="sc-search" style={{ marginBottom: 16 }}>
                                <i className="bx bx-search"></i>
                                <input value={patientSearchTerm} onChange={(e) => searchPatients(e.target.value)} placeholder="Search patients..." />
                            </div>

                            {recentlyCreatedPatient?.patient_id && (
                                <div style={{ ...cardStyle, background: ACCENT_SOFT, padding: 14, marginBottom: 14 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <div>
                                            <div style={{ fontWeight: 800 }}>Recently created patient</div>
                                            <div style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                {[recentlyCreatedPatient.first_name, recentlyCreatedPatient.last_name].filter(Boolean).join(' ')} · {recentlyCreatedPatient.patient_id}
                                            </div>
                                        </div>
                                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => choosePatient(recentlyCreatedPatient)}>
                                            <i className="bx bx-check me-1"></i>Use this patient
                                        </button>
                                    </div>
                                </div>
                            )}

                            {searchingPatients ? (
                                <div style={{ padding: 28, textAlign: 'center', color: 'var(--uzima-shell-text-muted)' }}>
                                    <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                    <div style={{ marginTop: 10 }}>Searching patients...</div>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {(patientSearchTerm ? patientResults : allPatients).map((patient, index) => (
                                        <div className="col-md-6" key={`${patient.patient_id || patient.id}-${index}`}>
                                            <button className="sc-btn sc-btn-ghost" onClick={() => choosePatient(patient)} style={{ ...cardStyle, width: '100%', padding: 16, justifyContent: 'flex-start', textAlign: 'left' }}>
                                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                                                    <div style={{ width: 44, height: 44, borderRadius: 14, background: BLUE_SOFT, color: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <i className="bx bx-user"></i>
                                                    </div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontWeight: 800 }}>{[patient.first_name, patient.last_name].filter(Boolean).join(' ') || 'Unnamed patient'}</div>
                                                        <div style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>{patient.patient_id || patient.id || 'No ID'}</div>
                                                        {patient.email && <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>{patient.email}</div>}
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!searchingPatients && !(patientSearchTerm ? patientResults : allPatients).length && (
                                <div style={{ padding: 28, textAlign: 'center', color: 'var(--uzima-shell-text-muted)' }}>
                                    {loadingPatients ? (
                                        <>
                                            <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                            <div style={{ marginTop: 10 }}>Loading patients...</div>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-search-alt" style={{ fontSize: '1.8rem', color: '#94a3b8' }}></i>
                                            <div style={{ marginTop: 10 }}>No patients found.</div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default ModernEHRCreate;
